function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { withRouter } from "react-router";
import { Container, FlexboxContainer, CssContainer } from "../../components/container";
import { PageHeader, Ribbon, Card, Pill, Empty, Search, Filters, Delta, Pending } from "../../components/analytics-kit";

// territory-common deltas ({text, dir, pct}) in the kit's shape; null stays null ("no comparison").
const kitDelta = d => d && d.pct != null ? {
  dir: d.dir,
  pct: d.pct
} : null;
import ComparisonPicker from "../../components/comparison-picker";
import StatusFilter from "../../components/status-filter";
import PlunkDropDown from "../../components/plunk-dropdown";
import { Loader, ChartLoader } from "../../components/loaders";
import Error from "../../components/error";
import Chart from "../../components/chart";
import { hasTenantFeature } from "../../functions/tenant-features";
import RangePicker from "../../components/range-picker";
import { rangeLabelFor, RANGES, num, usd, aov, fetchSales, bucketFor, trendLabel, stateName, deltaOf, deltaOfPaced, comparisonQs, hasPrevTrend, PREV_LINE_COLOR, channelLabel, apiComparison, accountLensKv, readAcctLens, writeAcctLens, readBasis, titleCase } from "./territory-common";
import { moneyShort, nameOf, place, dateShort } from "./rep-common";
import { rangeWithDates, displayTitle, dollars as moneyWhole } from "../products/px-common";
import account from "../../data/account";
import { AccountFilterControl, AccountFilterBanner } from "./account-filter";
import { ActivityComparisonMenu } from "../../components/container-activity-menus";
import { canViewQuotes, isAdminUser as isDeskAdmin, sendDigestNow, currentUserEmail } from "./sales-quotes-api";
import { isSoft } from "../audiences/api";
import { getDemandGaps, getDealerScorecards } from "./sales-api";
import { Link } from "react-router-dom";
import compLabel from "../../functions/comp-label";
import { getGlobalRange, getGlobalComp, subscribePeriod, unsubscribePeriod } from "../../functions/period-store";
import PlanCard from "./plan-card";
import SalesMapCard from "./map-card";

/*
 * Rep Detail — §5 of the Territory & Rep Intelligence spec ("customers,
 * territories, products, trend, status funnel for one rep").
 *
 * Route: /Territory/:range/rep/:repCode — opened from the overview rep
 * leaderboard, the market-detail rep table, and the attribution tables.
 *
 * Server-scoped where the 1.1.5 drill-through params allow:
 *   /sales/totals/{range}?rep=X   → rep KPI header (distinct counts)
 *   /sales/trend/{range}?rep=X    → rep sales trend
 *   /sales/rep/{range}/{X}        → per-product breakdown (top products)
 *   /sales/reps/{range}           → roster row (identity, coverage, book)
 *   /sales/attribution/{range}    → own- vs other-account split for X
 *
 * "Dealers Sold by this Rep" uses the 1.1.6 `?rep=` on /customers — the
 * transactions are filtered to this rep server-side, so the sales columns
 * are THIS REP's numbers per dealer (true sold-to roster, which replaced
 * the earlier client-side assigned-book approximation).
 */

class RepDetailViewComponent extends React.Component {
  constructor(props) {
    super(props);
    // Account lens (SALES_ACCOUNT_FILTER.md) — editable here; persists per-tenant
    // (shared with the overview) and re-scopes the rep's sections server-side.
    _defineProperty(this, "setAcctLens", (mode, ids) => {
      ids = [...new Set((ids || []).map(String))];
      writeAcctLens((account.get() || {}).tenantId, mode, ids);
      this.setState({
        acctMode: mode,
        acctIds: ids,
        data: null,
        error: false
      }, () => this.load());
    });
    _defineProperty(this, "removeAcct", id => this.setAcctLens(this.state.acctMode, this.state.acctIds.filter(x => String(x) !== String(id))));
    _defineProperty(this, "clearAcct", () => this.setAcctLens(this.state.acctMode, []));
    _defineProperty(this, "onRange", e => {
      this.props.history.push(`/Territory/${e.target.value}/rep/${encodeURIComponent(this.repCode())}`);
    });
    _defineProperty(this, "onCompSelected", e => {
      const comp = e.detail.item.dataset.value;
      if (comp === "unavailable" || comp === this.state.comp) return;
      this.setState({
        comp,
        data: null,
        error: false
      }, () => this.load());
    });
    _defineProperty(this, "back", () => {
      this.props.history.push(`/Territory/${this.range()}`);
    });
    // Rep digest test send (SALES_DIGESTS.md §5): POST /v1/sales/digests/rep/send-now { test, email, scope }; a missing route reads "not available yet".
    _defineProperty(this, "sendRepDigestTest", async () => {
      this.setState({
        digestBusy: true,
        digestNote: null
      });
      try {
        const r = await sendDigestNow("rep", {
          test: true,
          email: currentUserEmail(),
          scope: this.repCode()
        });
        const ok = !r || r.ok !== false;
        if (this._alive) this.setState({
          digestBusy: false,
          digestNote: ok ? `Test sent to ${currentUserEmail()}${r && r.subject ? `: ${r.subject}` : ""}` : `Test not sent: ${r && (r.error || r.text) || "the mail rail refused it"}`
        });
      } catch (e) {
        const msg = e && (e.message || e.body && e.body.error);
        if (this._alive) this.setState({
          digestBusy: false,
          digestNote: isSoft(e) ? "Digests are not available yet for this workspace" : `Test failed${msg ? `: ${msg}` : ""}`
        });
      }
      setTimeout(() => this._alive && this.setState({
        digestNote: null
      }), 12000);
    });
    _defineProperty(this, "openRegion", code => {
      this.props.history.push(`/Territory/${this.range()}/region/${encodeURIComponent(code)}`);
    });
    _defineProperty(this, "openCustomer", accountNumber => {
      if (!accountNumber) return;
      this.props.history.push(`/Territory/${this.range()}/customer/${encodeURIComponent(accountNumber)}`);
    });
    _defineProperty(this, "openProduct", p => {
      if (!p || !p.productId) return;
      this.props.history.push({
        pathname: `/Territory/${this.range()}/product/${encodeURIComponent(p.productId)}`,
        state: p.title ? {
          title: p.title
        } : undefined
      });
    });
    // "Their dealers" (board 3): Buying = this rep's sold-to roster; Not yet = assigned dealers with no orders in the
    // window, read lazily from the dealer scorecards (rep's book with last year beside it). Fail-soft.
    _defineProperty(this, "loadNotYet", async () => {
      if (this.state.notYet !== undefined) return;
      this.setState({
        notYet: "loading"
      });
      try {
        const env = await getDealerScorecards(this.range(), {
          rep: this.repCode(),
          comparison: "previous_year",
          top: 1000,
          sparkline: 0
        });
        if (!this._alive) return;
        if (!env) {
          this.setState({
            notYet: null
          });
          return;
        }
        const me = String(this.repCode()).trim().toLowerCase();
        const rows = env.items.filter(d => (Number(d.revenue) || 0) <= 0 && (!d.assignedRep || String(d.assignedRep.repCode || "").trim().toLowerCase() === me));
        this.setState({
          notYet: rows
        });
      } catch (e) {
        if (this._alive) this.setState({
          notYet: null
        });
      }
    });
    const lens = readAcctLens((account.get() || {}).tenantId);
    this.state = {
      data: null,
      error: false,
      status: [],
      comp: getGlobalComp("prev_period"),
      acctMode: lens.mode,
      acctIds: lens.ids,
      gaps: undefined
    };
    this._alive = true;
  }
  range() {
    return this.props.params && this.props.params.range || getGlobalRange();
  }
  repCode() {
    return decodeURIComponent(this.props.params && this.props.params.repCode || "");
  }
  componentDidMount() {
    this.load();
    this._periodTok = subscribePeriod(({
      range,
      comp
    }) => {
      if (!this._alive || !range) return;
      if (comp && comp !== this.state.comp) this.setState({
        comp
      });
      const target = `/Territory/${range}/rep/${encodeURIComponent(this.repCode())}`;
      if (window.location.pathname === target) {
        this.setState({
          data: null,
          error: false
        }, () => this.load());
      } else {
        this.props.history.push(target);
      }
    });
  }
  componentWillUnmount() {
    this._alive = false;
    unsubscribePeriod(this._periodTok);
  }
  componentDidUpdate(prev) {
    const prevKey = `${prev.params && prev.params.range || ""}|${prev.params && prev.params.repCode || ""}`;
    const key = `${this.props.params && this.props.params.range || ""}|${this.props.params && this.props.params.repCode || ""}`;
    if (prevKey !== key) {
      this.setState({
        data: null,
        error: false
      });
      this.load();
    }
  }
  async load() {
    const range = this.range();
    const comp = this.state.comp;
    const rep = encodeURIComponent(this.repCode());
    const bucket = bucketFor(range);
    const st = (this.state.status || []).join(",");
    const ws = u => st ? u + (u.includes("?") ? "&" : "?") + "status=" + encodeURIComponent(st) : u;
    // Account lens from state (editable via the on-page control); re-scopes the
    // rep's sections server-side. acctSource = UNSCOPED roster for the picker.
    const _acctKv = accountLensKv(this.state.acctMode, this.state.acctIds);
    // Address basis (bill-to / ship-to) rides on every territory call, like the account lens.
    const _basisKv = `basis=${readBasis((account.get() || {}).tenantId)}`;
    const wb = u => u + (u.includes("?") ? "&" : "?") + _basisKv;
    const wsa = u => {
      const x = ws(u);
      return wb(_acctKv ? x + (x.includes("?") ? "&" : "?") + _acctKv : x);
    };
    // Demand gaps scoped to this rep's dealers (?rep=); fail-soft and off the critical path.
    this.setState({
      gaps: undefined,
      notYet: undefined,
      dealerTab: "buying",
      dealersAll: false
    });
    getDemandGaps(range, {
      rep: this.repCode()
    }).then(gaps => this._alive && this.setState({
      gaps
    })).catch(() => this._alive && this.setState({
      gaps: null
    }));
    try {
      const [totals, trend, products, roster, attribution, customers, acctSource] = await Promise.all([fetchSales(wsa(`/sales/totals/${range}?rep=${rep}${comparisonQs(range, false, comp)}`)), fetchSales(wsa(`/sales/trend/${range}?bucket=${bucket}&rep=${rep}${comparisonQs(range, false, comp)}`)), fetchSales(wsa(`/sales/rep/${range}/${rep}`)), fetchSales(wb(`/sales/reps/${range}`)), fetchSales(wsa(`/sales/attribution/${range}`)), fetchSales(wsa(`/sales/customers/${range}?top=500&rep=${rep}`)), fetchSales(wb(ws(`/sales/customers/${range}?top=200`))).catch(() => [])]);
      if (this._alive) this.setState({
        data: {
          totals,
          trend,
          products,
          roster,
          attribution,
          customers,
          acctSource,
          bucket
        }
      });
    } catch (e) {
      if (this._alive) this.setState({
        error: true
      });
    }
  }
  trendChartData(trend, bucket) {
    if (!trend || trend.length < 2) return null;
    const withPrev = hasPrevTrend(trend);
    const data = [withPrev ? ["Period", "Revenue", "Prev Revenue", "Orders", "Prev Orders"] : ["Period", "Revenue", "Orders"]];
    trend.forEach(t => {
      const rev = Math.round((Number(t.totalAmount) || 0) * 100) / 100;
      const second = Number(t.orders) || 0;
      if (withPrev) data.push([trendLabel(t.period, bucket), rev, Math.round((Number(t.prevTotalAmount) || 0) * 100) / 100, second, Number(t.prevOrders) || 0]);else data.push([trendLabel(t.period, bucket), rev, second]);
    });
    data.withPrev = withPrev;
    return data;
  }
  render() {
    if (!hasTenantFeature("territoryRep")) {
      return /*#__PURE__*/React.createElement(CssContainer, {
        cls: "margin-squeeze"
      }, /*#__PURE__*/React.createElement("p", {
        className: "module-empty"
      }, "Territory & Rep Intelligence is not configured for this workspace."));
    }
    const {
      data,
      error
    } = this.state;
    if (error) return /*#__PURE__*/React.createElement(Error, null);
    if (!data) return /*#__PURE__*/React.createElement(Loader, null);
    const range = this.range();
    const repCode = this.repCode();
    const norm = v => String(v || "").trim();
    const {
      totals,
      trend,
      products,
      roster,
      attribution,
      customers,
      bucket
    } = data;
    const rep = roster.find(r => norm(r.repCode) === norm(repCode)) || {
      repCode,
      name: repCode
    };
    const repName = nameOf(rep.name, repCode);
    const attr = (attribution.byRep || []).find(r => norm(r.repCode) === norm(repCode));
    const states = String(rep.states || "").split(",").map(s => s.trim()).filter(Boolean);
    // Server-scoped sold-to roster (?rep=): sales columns are THIS rep's
    // numbers per dealer, already ranked by revenue.
    const soldTo = customers;
    const topProducts = (products || []).slice().sort((a, b) => (Number(b.totalAmount) || 0) - (Number(a.totalAmount) || 0)).slice(0, 15);
    const chartData = this.trendChartData(trend, bucket);

    // Tiles (board 3): revenue, orders + average, own dealers buying of those assigned, own-dealer share of orders.
    const assigned = Number(rep.assignedAccounts) || 0;
    const ownBuying = soldTo.filter(c => norm(c.assignedRepId) === norm(repCode)).length;
    const otherBuying = soldTo.length - ownBuying;
    const notYetCount = Math.max(0, assigned - ownBuying);
    const ownOrders = attr ? Number(attr.ownAccountOrders) || 0 : 0;
    const otherOrders = attr ? Number(attr.otherAccountOrders) || 0 : 0;
    const ownPct = ownOrders + otherOrders > 0 ? Math.round(ownOrders / (ownOrders + otherOrders) * 100) : null;
    const vsText = range === "all_time" ? "" : this.state.comp === "prev_year" ? "vs last year" : "vs previous period";
    const dRev = deltaOfPaced(totals.totalAmount, totals.prevTotalAmount, range);
    const dOrd = deltaOfPaced(totals.orders, totals.prevOrders, range);
    const pctTxt = d => d ? `${d.pct >= 0 ? "+" : "−"}${Math.abs(Math.round(d.pct))}%` : "";
    const avg = (Number(totals.orders) || 0) > 0 ? moneyWhole((Number(totals.totalAmount) || 0) / Number(totals.orders)) : "";
    const tiles = [{
      l: "Revenue",
      v: moneyShort(totals.totalAmount),
      s: dRev ? `${pctTxt(dRev)} ${vsText}`.trim() : "",
      dir: dRev ? dRev.dir : ""
    }, {
      l: "Orders",
      v: num(totals.orders),
      s: [dOrd ? pctTxt(dOrd) : "", avg ? `${avg} average` : ""].filter(Boolean).join(" · "),
      dir: dOrd ? dOrd.dir : ""
    }, assigned > 0 ? {
      l: "Dealers buying",
      v: `${num(ownBuying)} of ${num(assigned)}`,
      s: `${num(notYetCount)} assigned dealer${notYetCount === 1 ? "" : "s"} haven't ordered${otherBuying > 0 ? ` · plus ${num(otherBuying)} other reps' dealer${otherBuying === 1 ? "" : "s"}` : ""}`
    } : {
      l: "Dealers buying",
      v: num(soldTo.length),
      s: "no dealers assigned to this rep"
    }, {
      l: "Own dealers",
      v: ownPct == null ? "-" : `${ownPct}%`,
      s: ownPct == null ? "no orders to attribute yet" : "of their orders were from their own dealers"
    }];
    const subtitle = [`Rep ${rep.repCode || repCode}`, `${num(assigned)} dealer${assigned === 1 ? "" : "s"} assigned`, rep.status === "terminated" ? "no longer active" : null, rangeWithDates(range)].filter(Boolean).join(" · ");
    const base = `/Territory/${range}/rep/${encodeURIComponent(rep.repCode || repCode)}`;
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view territory-detail rep-detail tr-view wa-view trr-rep"
    }, /*#__PURE__*/React.createElement(PageHeader, {
      crumbs: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Link, {
        to: `/Territory/${range}`
      }, "Territory & Reps"), " \xB7 Rep"),
      title: repName,
      subtitle: subtitle,
      actions: /*#__PURE__*/React.createElement(React.Fragment, null, hasTenantFeature("quotes") && canViewQuotes() ? /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "trr-btn",
        onClick: () => this.props.history.push(`/Quotes?rep=${encodeURIComponent(rep.repCode || repCode)}`)
      }, "Their quotes") : null, isDeskAdmin() ? /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "trr-btn",
        disabled: this.state.digestBusy,
        title: `Send ${repName}'s weekly digest to you as a test`,
        onClick: this.sendRepDigestTest
      }, this.state.digestBusy ? "Sending…" : this.state.digestNote || "Send me their weekly digest") : null, /*#__PURE__*/React.createElement(StatusFilter, {
        range: range,
        value: this.state.status,
        onChange: v => this.setState({
          status: v,
          data: null,
          error: false
        }, () => this.load())
      }), /*#__PURE__*/React.createElement(AccountFilterControl, {
        customers: this.state.data && this.state.data.acctSource || [],
        mode: this.state.acctMode,
        ids: this.state.acctIds,
        onApply: this.setAcctLens
      }))
    }), /*#__PURE__*/React.createElement(AccountFilterBanner, {
      customers: this.state.data && this.state.data.acctSource || [],
      mode: this.state.acctMode,
      ids: this.state.acctIds,
      onRemove: this.removeAcct,
      onClear: this.clearAcct
    }), /*#__PURE__*/React.createElement("div", {
      className: "trr-tiles"
    }, tiles.map(t => /*#__PURE__*/React.createElement("div", {
      className: "trr-tile",
      key: t.l
    }, /*#__PURE__*/React.createElement("span", {
      className: "l"
    }, t.l), /*#__PURE__*/React.createElement("b", {
      className: "v"
    }, t.v), /*#__PURE__*/React.createElement("span", {
      className: `s${t.dir ? " " + t.dir : ""}`
    }, t.s)))), /*#__PURE__*/React.createElement("div", {
      className: "wa-grid"
    }, /*#__PURE__*/React.createElement(PlanCard, {
      kind: "rep",
      id: repCode,
      range: range,
      comparison: apiComparison(this.state.comp),
      span: 12
    }), chartData && /*#__PURE__*/React.createElement(Card, {
      title: "Sales trend",
      hint: "revenue bars \xB7 orders line \xB7 dashed = prior period when a comparison is set"
    }, /*#__PURE__*/React.createElement("div", {
      className: "territory-trend"
    }, /*#__PURE__*/React.createElement(Chart, {
      width: "100%",
      height: "320px",
      chartType: "ComboChart",
      loader: /*#__PURE__*/React.createElement(ChartLoader, null),
      data: chartData,
      options: {
        seriesType: "bars",
        series: chartData.withPrev ? {
          0: {
            targetAxisIndex: 0,
            color: "#ff6358" /* accent */,
            tooltipFormat: "currency"
          },
          1: {
            type: "line",
            targetAxisIndex: 0,
            color: "#aa46be" /* palette purple */,
            lineDashStyle: [4, 3],
            tooltipFormat: "currency"
          },
          2: {
            type: "line",
            targetAxisIndex: 1,
            color: "#78d237" /* palette green */,
            tooltipFormat: "{0:n0}"
          },
          3: {
            type: "line",
            targetAxisIndex: 1,
            color: "#ffd246" /* palette yellow */,
            lineDashStyle: [4, 3],
            tooltipFormat: "{0:n0}"
          }
        } : {
          0: {
            targetAxisIndex: 0,
            color: "#ff6358" /* accent */,
            tooltipFormat: "currency"
          },
          1: {
            type: "line",
            targetAxisIndex: 1,
            color: "#78d237" /* palette green */,
            tooltipFormat: "{0:n0}"
          }
        },
        vAxes: {
          0: {
            title: "Revenue",
            format: "short"
          },
          1: {
            title: "Orders",
            format: "0"
          }
        },
        legend: {
          position: "top"
        },
        backgroundColor: "transparent",
        chartArea: {
          left: 70,
          right: 60,
          top: 40,
          bottom: 40
        }
      }
    }))), states.length > 0 || rep.channel ? /*#__PURE__*/React.createElement("section", {
      className: "wa-card c12 trr-covers"
    }, /*#__PURE__*/React.createElement("b", {
      className: "trr-h"
    }, "Covers"), states.map(s => /*#__PURE__*/React.createElement("button", {
      type: "button",
      key: s,
      className: "trr-chip",
      onClick: () => this.openRegion(s),
      title: `Open ${stateName(s)} market detail`
    }, stateName(s))), rep.postalCodes ? /*#__PURE__*/React.createElement("span", {
      className: "trr-muted trr-small"
    }, "plus ", num(rep.postalCodes), " ZIP assignments") : null, rep.channel ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "trr-chip ghost",
      onClick: () => this.props.history.push(`/Territory/${range}/channel/${encodeURIComponent(rep.channel)}`),
      title: `Open ${channelLabel(rep.channel)} channel detail`
    }, channelLabel(rep.channel), " channel") : null) : null, /*#__PURE__*/React.createElement("section", {
      className: "wa-card c12 trr-drill"
    }, /*#__PURE__*/React.createElement("span", {
      className: "trr-drill-copy"
    }, /*#__PURE__*/React.createElement("b", null, "Look closer at what ", repName, " sells"), /*#__PURE__*/React.createElement("span", null, "See every product each dealer buys from them, and which products other reps' dealers buy that theirs don't.")), /*#__PURE__*/React.createElement("span", {
      className: "trr-drill-actions"
    }, /*#__PURE__*/React.createElement(Link, {
      className: "trr-btn",
      to: `${base}/dealers`
    }, "Products by dealer"), /*#__PURE__*/React.createElement(Link, {
      className: "trr-btn primary",
      to: `${base}/compare`
    }, "Compare with other reps"))), /*#__PURE__*/React.createElement(SalesMapCard, {
      range: range,
      rep: repCode,
      want: {
        coverage: true,
        dealers: true,
        heat: true
      },
      silentLoading: true,
      title: "Coverage, dealers and demand on the map",
      hint: "hexes = ZIPs this rep owns \xB7 pins = dealers sold by this rep, sized by revenue, coloured by lapse band \xB7 market heat = where the site sees interest against what is booked",
      onDealer: p => this.openCustomer(p.accountNumber || p.id),
      onRep: code => this.props.history.push(`/Territory/${range}/rep/${encodeURIComponent(code)}`)
    }), this.renderTheirDealers(repCode, soldTo, notYetCount), /*#__PURE__*/React.createElement(Card, {
      title: "What they sell most",
      span: 6,
      right: /*#__PURE__*/React.createElement(Link, {
        className: "trr-link",
        to: `${base}/dealers`
      }, "All products")
    }, /*#__PURE__*/React.createElement("div", {
      className: "trr-list"
    }, topProducts.slice(0, 8).map(p => /*#__PURE__*/React.createElement("button", {
      type: "button",
      key: p.productId,
      className: "trr-li",
      onClick: () => this.openProduct(p),
      title: `Open ${p.title || p.productId} product detail`
    }, /*#__PURE__*/React.createElement("span", {
      className: "trr-stack"
    }, /*#__PURE__*/React.createElement("b", null, [p.productId, displayTitle(p.title)].filter(Boolean).join(" · ")), /*#__PURE__*/React.createElement("span", {
      className: "trr-sub"
    }, num(p.quantity), " unit", Number(p.quantity) === 1 ? "" : "s", " \xB7 ", num(p.customers), " dealer", Number(p.customers) === 1 ? "" : "s")), /*#__PURE__*/React.createElement("span", null, moneyShort(p.totalAmount))))), topProducts.length === 0 && /*#__PURE__*/React.createElement(Empty, null, "No attributed product sales in this period.")), this.renderDemandGaps()));
  }
  renderTheirDealers(repCode, soldTo, notYetCount) {
    const tab = this.state.dealerTab || "buying";
    const norm = v => String(v || "").trim();
    const all = this.state.dealersAll;
    const nyRows = Array.isArray(this.state.notYet) ? this.state.notYet : [];
    const pick = t => {
      this.setState({
        dealerTab: t,
        dealersAll: false
      });
      if (t === "notyet") this.loadNotYet();
    };
    const list = tab === "buying" ? soldTo : nyRows;
    const shown = all ? list : list.slice(0, 8);
    return /*#__PURE__*/React.createElement(Card, {
      title: "Their dealers",
      span: 6,
      right: /*#__PURE__*/React.createElement("span", {
        className: "trr-pills",
        role: "group",
        "aria-label": "Their dealers"
      }, /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: tab === "buying" ? "on" : "",
        onClick: () => pick("buying")
      }, "Buying ", num(soldTo.length)), /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: tab === "notyet" ? "on" : "",
        onClick: () => pick("notyet")
      }, "Not yet ", num(notYetCount)))
    }, tab === "notyet" && (this.state.notYet === "loading" || this.state.notYet === undefined) ? /*#__PURE__*/React.createElement(Pending, {
      h: 160
    }) : tab === "notyet" && this.state.notYet === null ? /*#__PURE__*/React.createElement(Empty, null, notYetCount > 0 ? `${num(notYetCount)} assigned dealer${notYetCount === 1 ? " hasn't" : "s haven't"} ordered in this period. The list of who isn't available yet.` : "Every assigned dealer has ordered in this period.") : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "trr-list"
    }, shown.map(c => {
      const id = tab === "buying" ? c.accountNumber : c.accountNumber || c.customerId;
      const other = tab === "buying" && norm(c.assignedRepId) && norm(c.assignedRepId) !== norm(repCode);
      const sub = tab === "buying" ? [place(c.city, c.region), `${num(c.orders)} order${Number(c.orders) === 1 ? "" : "s"}`, other ? `assigned to ${nameOf(c.assignedRepName, c.assignedRepId)}` : ""] : [place(c.city, c.region), c.lastOrder ? `last order ${dateShort(c.lastOrder)}` : "no orders on record"];
      return /*#__PURE__*/React.createElement("button", {
        type: "button",
        key: id,
        className: "trr-li",
        onClick: () => this.openCustomer(id),
        title: `Open ${nameOf(c.name, id)} dealer page`
      }, /*#__PURE__*/React.createElement("span", {
        className: "trr-stack"
      }, /*#__PURE__*/React.createElement("b", null, nameOf(c.name, id)), /*#__PURE__*/React.createElement("span", {
        className: "trr-sub"
      }, sub.filter(Boolean).join(" · "))), /*#__PURE__*/React.createElement("span", null, tab === "buying" ? moneyShort(c.totalAmount) : c.prev && c.prev.revenue ? /*#__PURE__*/React.createElement("span", {
        className: "trr-muted"
      }, moneyShort(c.prev.revenue), " last year") : ""));
    })), !list.length ? /*#__PURE__*/React.createElement(Empty, null, tab === "buying" ? "No attributed dealer sales in this period." : notYetCount > 0 ? `${num(notYetCount)} assigned dealer${notYetCount === 1 ? " has" : "s have"} no orders on record yet.` : "Every assigned dealer has ordered in this period.") : null, tab === "notyet" && nyRows.length && notYetCount > nyRows.length ? /*#__PURE__*/React.createElement("p", {
      className: "trr-muted trr-small trr-pad"
    }, "Plus ", num(notYetCount - nyRows.length), " assigned dealer", notYetCount - nyRows.length === 1 ? "" : "s", " with no orders on record.") : null, list.length > 8 ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "trr-textlink",
      onClick: () => this.setState({
        dealersAll: !all
      })
    }, all ? "Show fewer" : `Show all ${num(list.length)}`) : null));
  }
}

// "Demand gaps in this territory": styles viewed far more than this rep's dealers order (top 8, ?rep= scoped).
// Hidden when the rail is absent (null); when viewsScope is "site" the views are site-wide and the note says so.
RepDetailViewComponent.prototype.renderDemandGaps = function renderDemandGaps() {
  const env = this.state.gaps;
  if (env === null) return null;
  const range = this.range();
  const trade = env && env.siteAudience === "trade";
  const siteWide = env && env.viewsScope === "site";
  const rows = env ? env.items.slice(0, 8) : [];
  const viewers = trade ? "designers and dealers" : "shoppers";
  return /*#__PURE__*/React.createElement(Card, {
    title: "Demand gaps in this territory",
    span: 12,
    hint: `${trade ? "styles the trade browses far more than this rep's dealers order" : "styles viewed far more than this rep's dealers order"} · top 8 · ${rangeLabelFor(range, RANGES)}`,
    right: /*#__PURE__*/React.createElement(Link, {
      className: "wa-rowlink",
      to: `/Products/${range}?grain=pricing`,
      title: "Open the Demand gaps page in Product Explorer"
    }, "All demand gaps")
  }, env === undefined ? /*#__PURE__*/React.createElement(Pending, {
    h: 140
  }) : !rows.length ? /*#__PURE__*/React.createElement(Empty, null, "No demand gaps for this territory in this period.") : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "tblwrap"
  }, /*#__PURE__*/React.createElement("table", {
    className: "territory-table wa-table"
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", {
    title: "The style; click for its product detail"
  }, "Product"), env.hasGap && /*#__PURE__*/React.createElement("th", {
    className: "n",
    title: "How far views run ahead of orders for this style against its category; the larger the number, the wider the gap"
  }, "Gap"), /*#__PURE__*/React.createElement("th", {
    className: "n",
    title: siteWide ? `Product page views by ${viewers}, site-wide` : `Product page views by ${viewers} in this territory`
  }, "Views"), /*#__PURE__*/React.createElement("th", {
    className: "n",
    title: "Units this rep's dealers ordered in the window"
  }, "Units"), /*#__PURE__*/React.createElement("th", {
    className: "n",
    title: "Units at the realised unit price"
  }, "Revenue"), /*#__PURE__*/React.createElement("th", {
    title: "Why this style is on the list: how it is ordered against its category, and where its price sits against the category median"
  }, "Reason"))), /*#__PURE__*/React.createElement("tbody", null, rows.map(r => /*#__PURE__*/React.createElement("tr", {
    key: r.productId,
    className: "clickable",
    onClick: () => this.props.history.push(`/Products/${range}/product/${encodeURIComponent(r.productId)}`),
    title: `Open ${r.title || r.productId} product detail`
  }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
    className: "name"
  }, titleCase(r.title) || r.productId), /*#__PURE__*/React.createElement("span", {
    className: "code"
  }, r.productId)), env.hasGap && /*#__PURE__*/React.createElement("td", {
    className: "n strong"
  }, r.gap != null ? num(Math.round(r.gap * 10) / 10) : /*#__PURE__*/React.createElement("span", {
    className: "dim"
  }, "-")), /*#__PURE__*/React.createElement("td", {
    className: "n"
  }, num(r.views)), /*#__PURE__*/React.createElement("td", {
    className: "n"
  }, num(r.units)), /*#__PURE__*/React.createElement("td", {
    className: "n strong"
  }, Number(r.revenue) > 0 ? moneyShort(r.revenue) : /*#__PURE__*/React.createElement("span", {
    className: "dim"
  }, "none")), /*#__PURE__*/React.createElement("td", {
    className: "wrap"
  }, r.reason ? /*#__PURE__*/React.createElement("span", {
    className: "hint",
    title: r.reason
  }, r.reason) : /*#__PURE__*/React.createElement("span", {
    className: "dim"
  }, "-"))))))), siteWide ? /*#__PURE__*/React.createElement("p", {
    className: "wa-foot"
  }, "Views are site-wide; units are this rep's dealers.") : null));
};
export default withRouter(RepDetailViewComponent);